import { ICON_TYPE } from "@/components/library/Icon";
import {
  LpFeatureStripBlock,
  LpHowItWorksBlock,
  LpReframeBlock,
  LpWorkflowsBlock,
} from "@/puck/landingBlocks";
import type { LandingComponents } from "@/puck/blocks/blockTypes";
import {
  ICON_TYPE_OPTIONS,
  SECTION_BACKGROUND_OPTIONS,
} from "@/puck/blocks/fieldOptions";

export const structureConfigs: Pick<
  LandingComponents,
  "LpFeatureStrip" | "LpReframe" | "LpHowItWorks" | "LpWorkflows"
> = {
  LpFeatureStrip: {
    label: "Feature strip (marketing)",
    fields: {
      items: {
        type: "array",
        min: 1,
        max: 4,
        getItemSummary: (item) => item.text,
        defaultItemProps: {
          iconName: "shield-check",
          iconType: ICON_TYPE.REGULAR,
          text: "Editable proof point",
        },
        arrayFields: {
          iconName: { type: "text", label: "Icon name" },
          iconType: { type: "select", label: "Icon style", options: ICON_TYPE_OPTIONS },
          text: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      items: [
        { iconName: "shield-check", iconType: ICON_TYPE.REGULAR, text: "Trained on your domain. Not retrofitted." },
        { iconName: "file-lines", iconType: ICON_TYPE.REGULAR, text: "Compliant by design." },
        { iconName: "message-lines", iconType: ICON_TYPE.REGULAR, text: "Voice, SMS, email, webchat, WhatsApp." },
        { iconName: "puzzle-piece", iconType: ICON_TYPE.REGULAR, text: "Connects to your stack." },
      ],
    },
    render: LpFeatureStripBlock,
  },
  LpReframe: {
    label: "Reframe (primary)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      cards: {
        type: "array",
        getItemSummary: (item) => item.label,
        defaultItemProps: { label: "Label", text: "Reframe point." },
        arrayFields: {
          label: { type: "text", contentEditable: true },
          text: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "The reframe",
      titleLead: "Your team is doing work that",
      titleAccent: "doesn't need a human.",
      cards: [
        { label: "Outbound", text: "People on the dialer all day." },
        { label: "Contact rates", text: "Most dials never connect." },
        { label: "After-hours", text: "People answer nights and weekends." },
        { label: "Hiring", text: "High turnover. Constant ramp." },
      ],
    },
    render: LpReframeBlock,
  },
  LpHowItWorks: {
    label: "How it works (steps)",
    fields: {
      eyebrow: { type: "text" },
      titleLead: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      steps: {
        type: "array",
        getItemSummary: (item) => item.title,
        defaultItemProps: {
          step: "Step 01",
          title: "Step title",
          description: "What happens in this step.",
          timeline: "Days 1–2",
        },
        arrayFields: {
          step: { type: "text" },
          title: { type: "text", contentEditable: true },
          description: { type: "textarea", contentEditable: true },
          timeline: { type: "text" },
        },
      },
    },
    defaultProps: {
      eyebrow: "How it works",
      titleLead: "Live on one queue in",
      titleAccent: "days, not months.",
      subtitle: "Comes pre-trained. Add it to a single queue and watch it work.",
      steps: [
        { step: "Step 01", title: "Map your queue.", description: "Pick one and script it with your team.", timeline: "Days 1–2" },
        { step: "Step 02", title: "Wire your stack.", description: "Native, read-write integrations.", timeline: "Days 3–7" },
        { step: "Step 03", title: "Go live.", description: "Pilot batch. You sign off on transcripts.", timeline: "Day 14 · Live" },
      ],
    },
    render: LpHowItWorksBlock,
  },
  LpWorkflows: {
    label: "Workflows (4-up)",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      background: { type: "select", options: SECTION_BACKGROUND_OPTIONS },
      topBorder: {
        type: "radio",
        label: "Top border",
        options: [
          { label: "On", value: true },
          { label: "Off", value: false },
        ],
      },
      items: {
        type: "array",
        getItemSummary: (item) => item.title,
        defaultItemProps: {
          iconName: "phone-volume",
          iconType: ICON_TYPE.REGULAR,
          number: "01",
          label: "Outbound",
          title: "Workflow title",
          description: "What this workflow does.",
          channels: [{ text: "Voice" }],
        },
        arrayFields: {
          iconName: { type: "text", label: "Icon name" },
          iconType: { type: "select", label: "Icon style", options: ICON_TYPE_OPTIONS },
          number: { type: "text" },
          label: { type: "text", contentEditable: true },
          title: { type: "text", contentEditable: true },
          description: { type: "textarea", contentEditable: true },
          channels: {
            type: "array",
            getItemSummary: (item) => item.text,
            defaultItemProps: { text: "Voice" },
            arrayFields: { text: { type: "text" } },
          },
        },
      },
    },
    defaultProps: {
      eyebrow: "Four workflows",
      title: "Start with one. Scale the rest in weeks.",
      subtitle: "Each workflow goes live in days.",
      background: "voiceCard",
      topBorder: true,
      items: [
        { iconName: "phone-volume", iconType: ICON_TYPE.REGULAR, number: "01", label: "Outbound", title: "Work every account.", description: "Reach the right party and resolve.", channels: [{ text: "Voice" }, { text: "SMS" }] },
        { iconName: "calculator", iconType: ICON_TYPE.REGULAR, number: "02", label: "Payments", title: "Negotiate and collect.", description: "Plans and payment inline.", channels: [{ text: "SMS" }, { text: "Voice" }] },
        { iconName: "phone-intercom", iconType: ICON_TYPE.REGULAR, number: "03", label: "Inbound", title: "Answer routine calls.", description: "Compliance rails by state.", channels: [{ text: "Voice" }, { text: "Webchat" }] },
        { iconName: "clock", iconType: ICON_TYPE.REGULAR, number: "04", label: "After-hours", title: "Cover nights and weekends.", description: "Same compliance, around the clock.", channels: [{ text: "Voice" }, { text: "SMS" }] },
      ],
    },
    render: LpWorkflowsBlock,
  },
};
